import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  Edit2,
  Plus,
  Shield,
  ShieldOff,
  RefreshCw,
  Trash2,
  Users,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "../../components/icons";
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { createCoachAccount } from "../../lib/auth";
import type { SubscriptionTier } from "../../types/auth";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Toast } from "../../components/Toast";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

interface Coach {
  id: string;
  userId: string;
  assessmentCode: string;
  tier: string;
  aiAnalysisAccess: boolean;
  manualAiAccess?: boolean;
  maxRespondents: number;
  accountPlan: string | null;
  isActive: boolean;
  partnerName: string | null;
  user: {
    fullName: string;
    email: string;
  };
  stats: {
    totalRespondents: number;
    completedAssessments: number;
    lastActiveDate: string | null;
  };
}

const columnHelper = createColumnHelper<Coach>();

export function CoachManagement() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [newCoach, setNewCoach] = useState({
    email: "",
    password: "",
    fullName: "",
    tier: "basic" as SubscriptionTier,
    respondentLimit: 25,
    locale: "en" as "en" | "tr",
  });

  const [editForm, setEditForm] = useState({
    tier: "basic" as SubscriptionTier,
    aiAnalysisAccess: false,
    manualAiAccess: false,
    respondentLimit: 50,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [aiFilter, setAiFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [togglingStatusIds, setTogglingStatusIds] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const columns = useMemo(() => [
    columnHelper.accessor('user.fullName', {
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-chocolate-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('user.email', {
      header: 'Email',
      cell: (info) => (
        <div className="text-chocolate-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('assessmentCode', {
      header: 'Assessment Code',
      cell: (info) => (
        <div className="font-mono text-sm text-chocolate-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('tier', {
      header: 'Tier',
      cell: (info) => (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-sage-100 text-sage-800 capitalize">
          {info.getValue().replace('_', ' ')}
        </span>
      ),
    }),
    columnHelper.accessor('accountPlan', {
      header: 'Plan',
      cell: (info) => {
        const plan = info.getValue();
        return (
          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${plan === 'FREE_PROFILE_COACH' ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-600'}`}>
            {plan === 'FREE_PROFILE_COACH' ? 'Free Profile' : 'Standard'}
          </span>
        );
      },
    }),
    columnHelper.accessor('partnerName', {
      header: 'Partner',
      cell: (info) => (
        info.getValue() || <span className="text-stone-400">No Partner</span>
      ),
    }),
    columnHelper.accessor('stats.totalRespondents', {
      header: 'Respondents',
      cell: (info) => (
        <div className="text-chocolate-900 font-medium">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('maxRespondents', {
      header: 'Limit',
      cell: (info) => (
        <div className="text-chocolate-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('stats.completedAssessments', {
      header: 'Completed',
      cell: (info) => (
        <div className="text-chocolate-900 font-medium">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('stats.lastActiveDate', {
      header: 'Last Active',
      cell: (info) => {
        const val = info.getValue();
        if (!val) return <span className="text-chocolate-300 text-sm">Never</span>;
        const date = new Date(val);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const isInactive = date < thirtyDaysAgo;
        return (
          <span className={`text-sm ${isInactive ? 'text-coral-600' : 'text-chocolate-600'}`}>
            {date.toLocaleDateString()}
          </span>
        );
      },
    }),
    columnHelper.accessor('aiAnalysisAccess', {
      header: 'AI Access',
      cell: (info) => {
        const coach = info.row.original;
        const hasAnyAI = coach.aiAnalysisAccess || coach.manualAiAccess;
        return (
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            hasAnyAI
              ? 'bg-sage-100 text-sage-800'
              : 'bg-cream-200 text-chocolate-600'
          }`}>
            {hasAnyAI ? 'Enabled' : 'Disabled'}
          </span>
        );
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      cell: (info) => {
        const coach = info.row.original;
        return (
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            coach.isActive
              ? 'bg-success-10 text-success-60'
              : 'bg-coral-100 text-coral-600'
          }`}>
            {coach.isActive ? 'Active' : 'Disabled'}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const coach = info.row.original;
        const isToggling = togglingStatusIds.has(coach.id);
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditClick(coach)}
              className="p-2 rounded-lg bg-sage-100 text-sage-600 hover:bg-sage-200 transition-all"
              title="Edit Coach"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleToggleActive(coach)}
              disabled={isToggling}
              className={`p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                coach.isActive
                  ? 'bg-coral-100 text-coral-600 hover:bg-coral-200'
                  : 'bg-success-10 text-success-60 hover:bg-success-20'
              }`}
              title={coach.isActive ? 'Disable Access' : 'Enable Access'}
            >
              {isToggling ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : coach.isActive ? (
                <ShieldOff className="w-4 h-4" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
            </button>
          </div>
        );
      },
    }),
  ], [togglingStatusIds]);

  const filteredData = useMemo(() => {
    return coaches.filter(coach => {
      if (tierFilter !== "all" && coach.tier !== tierFilter) {
        return false;
      }

      if (aiFilter === "enabled" && !coach.aiAnalysisAccess && !coach.manualAiAccess) {
        return false;
      }
      if (aiFilter === "disabled" && (coach.aiAnalysisAccess || coach.manualAiAccess)) {
        return false;
      }

      if (planFilter === "free" && coach.accountPlan !== "FREE_PROFILE_COACH") {
        return false;
      }
      if (planFilter === "standard" && coach.accountPlan === "FREE_PROFILE_COACH") {
        return false;
      }

      if (statusFilter === "active" && !coach.isActive) {
        return false;
      }
      if (statusFilter === "disabled" && coach.isActive) {
        return false;
      }

      return true;
    });
  }, [coaches, tierFilter, aiFilter, planFilter, statusFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    if (user?.id) {
      loadCoaches();
    }
  }, [user]);

  const loadCoaches = async () => {
    try {
      setIsLoading(true);

      const { data: coachesData, error: coachesError } = await supabase
        .from('coaches')
        .select('id, user_id, assessment_code, tier, ai_analysis_access, manual_ai_access, max_respondents, account_plan, partner_id, is_active');

      if (coachesError) throw coachesError;

      const coachesWithDetails = await Promise.all(
        (coachesData || []).map(async (coach) => {
          const [userRes, respondentsRes, completionsRes, lastResultRes, partnerRes] = await Promise.all([
            supabase
              .from('users')
              .select('full_name, email')
              .eq('id', coach.user_id)
              .maybeSingle(),
            supabase
              .from('respondents')
              .select('*', { count: 'exact', head: true })
              .eq('coach_id', coach.id),
            supabase
              .from('results')
              .select('*', { count: 'exact', head: true })
              .eq('coach_id', coach.id),
            supabase
              .from('results')
              .select('completed_at')
              .eq('coach_id', coach.id)
              .order('completed_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
            coach.partner_id
              ? supabase.from('partners').select('company_name').eq('id', coach.partner_id).maybeSingle()
              : Promise.resolve({ data: null, error: null }),
          ]);

          return {
            id: coach.id,
            userId: coach.user_id,
            assessmentCode: coach.assessment_code,
            tier: coach.tier,
            aiAnalysisAccess: coach.ai_analysis_access || false,
            manualAiAccess: coach.manual_ai_access || false,
            maxRespondents: coach.max_respondents || 50,
            accountPlan: coach.account_plan || null,
            isActive: coach.is_active !== false,
            partnerName: partnerRes.data?.company_name || null,
            user: {
              fullName: userRes.data?.full_name || "",
              email: userRes.data?.email || "",
            },
            stats: {
              totalRespondents: respondentsRes.count || 0,
              completedAssessments: completionsRes.count || 0,
              lastActiveDate: lastResultRes.data?.completed_at || null,
            },
          };
        })
      );

      setCoaches(coachesWithDetails);
    } catch (err) {
      console.error("Error loading coaches:", err);
      setError("Failed to load coaches");
    } finally {
      setIsLoading(false);
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleAddCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);
      setError("");

      await createCoachAccount(null, {
        ...newCoach,
        assessmentCode: generateAssessmentCode(),
        partnerId: "",
        maxRespondents: newCoach.respondentLimit,
        locale: newCoach.locale,
      });

      setShowAddModal(false);
      setNewCoach({ email: "", password: "", fullName: "", tier: "basic", respondentLimit: 25, locale: "en" });
      loadCoaches();
      setSuccessMessage('Coach account created successfully! Login details have been emailed to the coach.');
    } catch (err: any) {
      console.error("Error adding coach:", err);
      setError(err.message || "Failed to add coach");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = (coach: Coach) => {
    setSelectedCoach(coach);
    setEditForm({
      tier: coach.tier as SubscriptionTier,
      aiAnalysisAccess: coach.aiAnalysisAccess,
      manualAiAccess: coach.manualAiAccess || false,
      respondentLimit: coach.maxRespondents,
    });
    setShowEditModal(true);
  };

  const handleToggleActive = async (coach: Coach) => {
    setTogglingStatusIds(prev => new Set(prev).add(coach.id));
    try {
      const { error } = await supabase
        .from('coaches')
        .update({
          is_active: !coach.isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', coach.id);

      if (error) throw error;

      setSuccessMessage(
        coach.isActive
          ? `Access disabled for ${coach.user.fullName}`
          : `Access enabled for ${coach.user.fullName}`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadCoaches();
    } catch (err) {
      console.error('Error toggling coach access:', err);
      setError('Failed to update coach access');
    } finally {
      setTogglingStatusIds(prev => {
        const next = new Set(prev);
        next.delete(coach.id);
        return next;
      });
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCoach) return;

    try {
      setIsLoading(true);
      setError("");

      const coachTier = editForm.tier === 'admin' ? 'advanced' : editForm.tier;
      const { error: updateError } = await supabase
        .from('coaches')
        .update({
          tier: coachTier as 'basic' | 'basic_plus' | 'advanced' | 'partner',
          ai_analysis_access: ["advanced", "partner"].includes(editForm.tier),
          manual_ai_access: editForm.tier === "basic_plus",
          max_respondents: editForm.respondentLimit,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedCoach.id);

      if (updateError) throw updateError;

      setShowEditModal(false);
      await loadCoaches();
    } catch (err) {
      console.error("Error updating coach:", err);
      setError("Failed to update coach");
    } finally {
      setIsLoading(false);
    }
  };

  const generateAssessmentCode = () => {
    return "HPOS" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading coaches..." />;
  }

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-coral-500 rounded-2xl flex items-center justify-center shadow-soft">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-2xl text-chocolate-900">
                Coach Management
              </h1>
              <p className="freud-text-base text-chocolate-500">Create and manage coach accounts</p>
            </div>
          </div>
          <button
            onClick={() => { setError(""); setShowAddModal(true); }}
            className="freud-btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Coach
          </button>
        </div>

        <Toast
          isVisible={!!successMessage}
          message={successMessage || ''}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />

        {error && (
          <div className="flex items-center gap-2 bg-coral-100 text-coral-700 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="freud-card-elevated p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-chocolate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search coaches..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="freud-input pl-10"
              />
            </div>

            <div>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="freud-input"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="basic_plus">Basic Plus</option>
                <option value="advanced">Advanced</option>
                <option value="partner">Partner</option>
              </select>
            </div>

            <div>
              <select
                value={aiFilter}
                onChange={(e) => setAiFilter(e.target.value)}
                className="freud-input"
              >
                <option value="all">All AI Status</option>
                <option value="enabled">AI Enabled</option>
                <option value="disabled">AI Disabled</option>
              </select>
            </div>

            <div>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="freud-input"
              >
                <option value="all">All Plans</option>
                <option value="free">Free Profile</option>
                <option value="standard">Standard</option>
              </select>
            </div>
            <div>
              <label className="freud-label block text-chocolate-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="freud-input"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="freud-card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100 border-b border-cream-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-chocolate-900 cursor-pointer hover:bg-cream-200"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' up',
                            desc: ' down',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-cream-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-chocolate-400">
                      {coaches.length === 0 ? "No coaches found" : "No coaches match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hover:bg-cream-50 transition-colors"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-cream-200 bg-cream-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-chocolate-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>

                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-chocolate-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-cream-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-chocolate-600">of {table.getPageCount()}</span>
                </div>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-cream-300 text-chocolate-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>

                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-cream-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="freud-card-elevated p-8 w-full max-w-lg mx-4"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="freud-heading-xl text-chocolate-900">Add New Coach</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 rounded-lg bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddCoach} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="freud-label block text-chocolate-900 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={newCoach.fullName}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        fullName: e.target.value,
                      }))
                    }
                    className="freud-input"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="freud-label block text-chocolate-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newCoach.email}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="freud-input"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="freud-label block text-chocolate-900 mb-2">
                    Initial Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newCoach.password}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="freud-input"
                    required
                    minLength={8}
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="tier" className="freud-label block text-chocolate-900 mb-2">
                    Subscription Tier
                  </label>
                  <select
                    id="tier"
                    name="tier"
                    value={newCoach.tier}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        tier: e.target.value as SubscriptionTier,
                      }))
                    }
                    className="freud-input"
                    disabled={isLoading}
                  >
                    <option value="basic">Basic</option>
                    <option value="basic_plus">Basic Plus</option>
                    <option value="advanced">Advanced</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="respondentLimit" className="freud-label block text-chocolate-900 mb-2">
                    Respondent Limit
                  </label>
                  <input
                    type="number"
                    id="respondentLimit"
                    value={newCoach.respondentLimit}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        respondentLimit: parseInt(e.target.value) || 25,
                      }))
                    }
                    className="freud-input"
                    min={1}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="locale" className="freud-label block text-chocolate-900 mb-2">
                    Language / Region
                  </label>
                  <select
                    id="locale"
                    value={newCoach.locale}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        locale: e.target.value as "en" | "tr",
                      }))
                    }
                    className="freud-input"
                    disabled={isLoading}
                  >
                    <option value="en">English</option>
                    <option value="tr">Turkish</option>
                  </select>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="freud-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="freud-btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Shield className="w-5 h-5" />
                    {isSaving ? "Adding..." : "Add Coach"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showEditModal && selectedCoach && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="freud-card-elevated p-8 w-full max-w-lg mx-4"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="freud-heading-xl text-chocolate-900">Edit Coach</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 rounded-lg bg-cream-200 text-chocolate-700 hover:bg-cream-300 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-cream-100 rounded-xl">
                <h3 className="freud-heading-base text-chocolate-900 mb-2">
                  {selectedCoach.user.fullName}
                </h3>
                <p className="freud-text-base text-chocolate-600">{selectedCoach.user.email}</p>
                <p className="freud-text-sm text-chocolate-500">Code: {selectedCoach.assessmentCode}</p>
              </div>

              <form onSubmit={handleEditSubmit} className="space-y-6">
                <div>
                  <label htmlFor="editTier" className="freud-label block text-chocolate-900 mb-2">
                    Subscription Tier
                  </label>
                  <select
                    id="editTier"
                    value={editForm.tier}
                    onChange={(e) => setEditForm(prev => ({ ...prev, tier: e.target.value as SubscriptionTier }))}
                    className="freud-input"
                    disabled={isLoading}
                  >
                    <option value="basic">Basic</option>
                    <option value="basic_plus">Basic Plus</option>
                    <option value="advanced">Advanced</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="editRespondentLimit" className="freud-label block text-chocolate-900 mb-2">
                    Respondent Limit
                  </label>
                  <input
                    type="number"
                    id="editRespondentLimit"
                    value={editForm.respondentLimit}
                    onChange={(e) => setEditForm(prev => ({ ...prev, respondentLimit: parseInt(e.target.value) || 50 }))}
                    className="freud-input"
                    min={1}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="bg-sage-50 p-4 rounded-xl">
                  <h4 className="text-sm font-medium text-chocolate-900 mb-2">Access Levels:</h4>
                  <ul className="text-sm text-chocolate-600 space-y-1">
                    <li>- Basic: No AI access</li>
                    <li>- Basic Plus: Manual AI analysis only</li>
                    <li>- Advanced: Full AI analysis access</li>
                    <li>- Partner: Full AI analysis access</li>
                  </ul>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="freud-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="freud-btn-primary flex items-center gap-2"
                  >
                    <Shield className="w-5 h-5" />
                    {isLoading ? "Updating..." : "Update Coach"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
